/**
 * Färgmatematik för temakontroller.
 *
 * Delas av två konsumenter som annars hade glidit isär:
 *   - `scripts/validate_themes.mjs` (node, kör mot CSS-texten före bygget)
 *   - `components/admin/AdminThemes.jsx` (webbläsaren, mäter renderade värden)
 *
 * En admin som får "godkänt" i labbet medan CI säger nej är värre än ingen
 * kontroll alls, så båda måste räkna med samma funktioner och samma trösklar.
 *
 * Filen importeras av node som ESM trots att `frontend/package.json` saknar
 * `"type": "module"` — samma sak gäller redan `themes/index.js`, som
 * validatorn importerar. Går det sönder i en framtida nodversion smäller
 * validatorn högljutt, vilket är rätt sätt att få reda på det.
 */

/** WCAG AA för brödtext. */
export const AA_TEXT = 4.5;

/** WCAG AA för grafiska element. */
export const AA_GRAPHIC = 3.0;

/**
 * Golv för nedtonad text.
 *
 * Mörka temat — som är facit och ligger i drift — har `--text-muted` på
 * 3,20:1 mot sidbakgrunden och 2,94:1 mot kortytan. Det når inte AA, och det
 * är känd skuld. Men en kontroll som underkänner det som redan är shippat
 * blir ignorerad, och då fångar den ingenting alls.
 *
 * Golvet betyder därför "inte sämre än vad vi redan levererar", inte "det här
 * är bra". Höj det när mörka temats muted-text åtgärdas.
 */
export const MUTED_MIN = 2.9;

/**
 * Minsta perceptuella avstånd mellan kategoriska diagramfärger.
 *
 * Kontrastkvot är fel mått här: den mäter bara ljushetsskillnad, och blått
 * `#3b82f6` mot orange `#e06000` ligger på 1,02:1 trots att de är uppenbart
 * olika färger. ~25 skiljer tryggt åt serier utan att kräva att paletten
 * spretar.
 */
export const CHART_MIN_DELTA_E = 25;

/**
 * Relativ luminans enligt WCAG.
 *
 * @param {[number, number, number]} rgb
 * @returns {number}
 */
export function luminance([r, g, b]) {
  const f = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/**
 * Kontrastkvot mellan två färger.
 *
 * @param {[number, number, number]} a
 * @param {[number, number, number]} b
 * @returns {number} 1–21.
 */
export function contrast(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * sRGB → CIE Lab (D65), för perceptuellt färgavstånd.
 *
 * @param {[number, number, number]} rgb
 * @returns {[number, number, number]} [L, a, b]
 */
export function toLab([r, g, b]) {
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [R, G, B] = [lin(r), lin(g), lin(b)];

  // sRGB → XYZ, normaliserat mot D65-vitpunkten.
  const x = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
  const y = (R * 0.2126 + G * 0.7152 + B * 0.0722) / 1.0;
  const z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;

  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const [fx, fy, fz] = [f(x), f(y), f(z)];

  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/**
 * Perceptuellt färgavstånd (CIE76).
 *
 * @param {[number, number, number]} a
 * @param {[number, number, number]} b
 * @returns {number}
 */
export function deltaE(a, b) {
  const [l1, a1, b1] = toLab(a);
  const [l2, a2, b2] = toLab(b);
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2);
}

/**
 * Tolkar #rgb, #rrggbb och rgb()/rgba() till RGB-trippel.
 *
 * Alfa ignoreras med flit: ett halvgenomskinligt värde kan inte mätas utan
 * att veta vad som ligger under, och de tokens som mäts är opaka.
 * Webbläsaren normaliserar alltid till `rgb()`/`rgba()`, CSS-filerna använder
 * mest hex — båda formerna måste därför gå igenom här.
 *
 * @param {string} value
 * @returns {[number, number, number] | null} null om värdet inte är en färg.
 */
export function parseColor(value) {
  const v = (value || '').trim();

  const hex = v.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1];
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  }

  const rgb = v.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const parts = rgb[1].split(/[,\s/]+/).filter(Boolean).map(Number);
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => Number.isFinite(n))) {
      return parts.slice(0, 3);
    }
  }

  return null;
}
